/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';

import LoadingIndicator from './uiold/LoadingIndicator';
import { postJSON } from '../support/Fetch';
import {
  Form,
  Link,
  SubmitButton,
  Text,
  TextInput,
  TextInputType,
  TextInputPassword,
} from './uiold';
import { themeColors, basicColors } from './uiold/Constants';
import { Account } from '../types/shared/Account';
import { User } from '../types/shared/User';
import { useShowMessage } from '../core-ui';

type Props = {
  onLoginSuccess: (account: Account, user: User) => void;
  onForgotPasswordClick: () => void;
};

function LoginForm(props: Props) {
  const showMessage = useShowMessage();
  const usernameInputRef = useRef<TextInputType>(null);
  const passwordInputRef = useRef<TextInputPassword>(null);

  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Focus either the username or the password field
  const focus = useCallback(() => {
    if (usernameInputRef?.current?.getNativeHandle()?.value) {
      passwordInputRef.current?.focus();
    } else {
      usernameInputRef.current?.focus();
    }
  }, [usernameInputRef, passwordInputRef]);

  // Automatically set the focus after the form first mounts
  useEffect(() => {
    let timeout = setTimeout(focus, 50);
    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attemptLogin = (username: string, password: string) => {
    let reqData = { username, password };
    postJSON('/api/auth', reqData, (error, data) => {
      if (error) {
        setIsAuthenticating(false);
        showMessage('error', t('Network or server error. Check connection.'));
      } else if (data.success) {
        let user = data.user;
        let account = {
          ...data.account,
          token: data.token,
          system_admin: data.system_admin,
        };
        props.onLoginSuccess(account, user);
      } else {
        let message = data.suspended
          ? t('Account suspended')
          : t('Invalid username or password');
        showMessage('error', message);
        setIsAuthenticating(false);
        setTimeout(focus, 50);
      }
    });
  };

  return (
    <View style={styles.root}>
      <Form
        style={styles.mainView}
        onSubmit={() => {
          attemptLogin(username, password);
          setIsAuthenticating(true);
        }}
      >
        <Text style={styles.loginTitle}>{t('Login')}</Text>
        {isAuthenticating ? <LoadingIndicator /> : null}
        <TextInput
          testID="input-username"
          ref={usernameInputRef}
          style={[styles.formControlFocus, styles.formControl]}
          placeholder={t('Username')}
          value={username}
          onChangeText={(value) => setUsername(value)}
        />
        <TextInputPassword
          testID="input-password"
          containerStyle={{
            marginTop: 15,
            width: '100%',
          }}
          ref={passwordInputRef}
          style={[styles.formControlFocus, styles.formControl]}
          placeholder={t('Password')}
          value={password}
          onChangeText={(value) => setPassword(value)}
        />
        <View style={styles.loginForgotContainer}>
          <Link
            style={styles.forgotPassword}
            hoverStyle={styles.active}
            onPress={props.onForgotPasswordClick}
          >
            {t('Forgot Password?')}
          </Link>
          <SubmitButton
            testID="button-login"
            style={styles.loginButton}
            hoverStyle={styles.hoveredColor}
            title={t('Login')}
            textStyle={styles.loginButtonText}
          />
        </View>
      </Form>
    </View>
  );
}

let styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  mainView: {
    paddingHorizontal: 30,
    paddingTop: 10,
    flex: 1,
    alignItems: 'center',
  },
  loginTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: 28,
    fontWeight: '300',
    color: themeColors.formTitle,
    marginBottom: 15,
    marginTop: 20,
  },
  loginButtonText: {
    fontWeight: '600',
    textTransform: 'uppercase',
    color: basicColors.white,
  },
  loginForgotContainer: {
    marginTop: 20,
    marginBottom: 25,
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'space-between',
    width: '100%',
  },
  loginButton: {
    marginHorizontal: 0,
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: themeColors.formButtonBackground,
    borderColor: themeColors.formButtonBorder,
    borderRadius: 3,
  },
  forgotPassword: {
    alignItems: 'flex-end',
    marginTop: 10,
    color: themeColors.blue,
    fontWeight: '400',
  },
  active: {
    textDecorationLine: 'underline',
  },
  formControl: {
    backgroundColor: themeColors.textInputBackground,
    height: 43,
    color: themeColors.textInputText,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: themeColors.borderGrey,
  },
  formControlFocus: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: themeColors.focusTextInputBorder,
  },
  hoveredColor: {
    backgroundColor: themeColors.greenButtonHovered,
    borderColor: themeColors.buttonHoveredBorder,
  },
});

export default LoginForm;
